import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Ban,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  HardHat,
  History,
  Info,
  Loader2,
  Package,
  PauseCircle,
  Pencil,
  PlayCircle,
  ShieldCheck,
  Star,
  UserCheck,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import type {
  InterventionEtatFinal,
  InterventionModeAffectation,
  InterventionPriorite,
  InterventionStatut,
  InterventionTypeDemande,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { DocumentsList } from '../batiments/DocumentsList';
import { PdfExportButton } from '../shared/PdfExportButton';
import { InterventionFormModal } from './InterventionFormModal';
import { ValidationsPanel } from './ValidationsPanel';
import { PiecesPanel } from './PiecesPanel';
import { InterventionHistoryTimeline } from './InterventionHistoryTimeline';
import {
  EtatFinalBadge,
  PrioriteBadge,
  StatutInterventionBadge,
  TypeDemandeBadge,
  formatDuration,
  typeDemandeLabel,
} from './badges';
import {
  AffecterModal,
  AnnulerModal,
  CloturerModal,
  DemarrerModal,
  EvaluerModal,
  PauseModal,
  PlanifierModal,
} from './WorkflowModals';

/**
 * Drawer 360° intervention (Phase 11 — toutes US workflow).
 *
 *  Onglets : Informations · Workflow · Validations · Pièces · Documents ·
 *  Évaluation · Historique.
 */

interface InterventionDetail {
  id: string;
  tenant_id: string;
  numero_demande: string;
  titre: string;
  description: string;
  type_demande: InterventionTypeDemande;
  priorite: InterventionPriorite;
  statut: InterventionStatut;
  demandeur_id: string;
  service_demandeur_id: string | null;
  date_demande: string;
  equipement_id: string | null;
  mode_affectation: InterventionModeAffectation | null;
  technicien_affecte_id: string | null;
  equipe_affectee: string[];
  prestataire_id: string | null;
  date_affectation: string | null;
  date_planifiee: string | null;
  duree_estimee: number | null;
  date_debut_intervention: string | null;
  date_fin_intervention: string | null;
  duree_reelle: number | null;
  diagnostic_initial: string | null;
  diagnostic_final: string | null;
  cause_panne: string | null;
  actions_realisees: string | null;
  etat_final: InterventionEtatFinal | null;
  cout_main_oeuvre: number | null;
  cout_pieces: number | null;
  cout_deplacement: number | null;
  cout_total: number | null;
  note_satisfaction: number | null;
  commentaire_satisfaction: string | null;
  evaluation_delai: number | null;
  evaluation_qualite: number | null;
  evaluation_comportement: number | null;
  date_evaluation: string | null;
  custom_fields: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  demandeur: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  service_demandeur: {
    id: string;
    code: string;
    nom_complet: string;
  } | null;
  equipement: {
    id: string;
    code_equipement: string;
    designation: string;
    statut: string;
  } | null;
  technicien: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  prestataire: {
    id: string;
    nom: string;
    specialite: string | null;
  } | null;
}

type Tab =
  | 'info'
  | 'workflow'
  | 'validations'
  | 'pieces'
  | 'documents'
  | 'evaluation'
  | 'historique';

type WorkflowAction =
  | 'affecter'
  | 'planifier'
  | 'demarrer'
  | 'pause'
  | 'reprendre'
  | 'cloturer'
  | 'annuler';

export function InterventionDetailDrawer({
  interventionId,
  onClose,
  onRefresh,
}: {
  interventionId: string;
  onClose(): void;
  onRefresh?(): void;
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canUpdate = can('interventions', 'update');

  const [detail, setDetail] = useState<InterventionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('info');
  const [editOpen, setEditOpen] = useState(false);
  const [workflowAction, setWorkflowAction] = useState<WorkflowAction | null>(null);
  const [evaluerOpen, setEvaluerOpen] = useState(false);
  const [reprendreLoading, setReprendreLoading] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: InterventionDetail }>(`/interventions/${interventionId}`);
      setDetail(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventionId]);

  async function handleReprendre() {
    setReprendreLoading(true);
    try {
      await api.post(`/interventions/${interventionId}/reprendre`);
      refresh();
      onRefresh?.();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setReprendreLoading(false);
    }
  }

  function onWorkflowSaved() {
    setWorkflowAction(null);
    refresh();
    onRefresh?.();
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center min-w-0">
            <HardHat className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
            <span className="truncate">{detail?.titre ?? 'Intervention'}</span>
            {detail && (
              <span className="ml-2 text-xs font-medium text-gray-500 uppercase tracking-wide flex-shrink-0">
                {detail.numero_demande}
              </span>
            )}
          </h2>
          <div className="flex items-center space-x-1">
            {detail && (
              <PdfExportButton
                targetRef={pdfRef}
                filename={`fiche-intervention-${detail.numero_demande}.pdf`}
                label="PDF"
                onBeforeExport={() => setTab('info')}
              />
            )}
            {canUpdate && detail && detail.statut === 'nouveau' && (
              <button
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Pencil className="w-3.5 h-3.5 mr-1" />
                Modifier
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {detail && (
          <div className="flex items-center space-x-2 px-4 py-2 border-b border-gray-100 flex-shrink-0 flex-wrap gap-1">
            <PrioriteBadge priorite={detail.priorite} />
            <StatutInterventionBadge statut={detail.statut} />
            <TypeDemandeBadge type={detail.type_demande} />
          </div>
        )}

        <div className="border-b border-gray-200 flex-shrink-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <nav className="flex">
            <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
              <Info className="w-3.5 h-3.5 inline mr-1" />
              Informations
            </TabButton>
            <TabButton active={tab === 'workflow'} onClick={() => setTab('workflow')}>
              <Wrench className="w-3.5 h-3.5 inline mr-1" />
              Workflow
            </TabButton>
            <TabButton active={tab === 'validations'} onClick={() => setTab('validations')}>
              <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
              Validations
            </TabButton>
            <TabButton active={tab === 'pieces'} onClick={() => setTab('pieces')}>
              <Package className="w-3.5 h-3.5 inline mr-1" />
              Pièces
            </TabButton>
            <TabButton active={tab === 'documents'} onClick={() => setTab('documents')}>
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              Documents
            </TabButton>
            <TabButton active={tab === 'evaluation'} onClick={() => setTab('evaluation')}>
              <Star className="w-3.5 h-3.5 inline mr-1" />
              Évaluation
            </TabButton>
            <TabButton active={tab === 'historique'} onClick={() => setTab('historique')}>
              <History className="w-3.5 h-3.5 inline mr-1" />
              Historique
            </TabButton>
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {loading || !detail ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : tab === 'info' ? (
            <div ref={pdfRef}>
              <InfoTab detail={detail} />
            </div>
          ) : tab === 'workflow' ? (
            <WorkflowTab
              detail={detail}
              canUpdate={canUpdate}
              reprendreLoading={reprendreLoading}
              onAction={setWorkflowAction}
              onReprendre={handleReprendre}
            />
          ) : tab === 'validations' ? (
            <ValidationsPanel
              interventionId={interventionId}
              onRefresh={() => {
                refresh();
                onRefresh?.();
              }}
            />
          ) : tab === 'pieces' ? (
            <PiecesPanel
              interventionId={interventionId}
              onRefresh={() => {
                refresh();
                onRefresh?.();
              }}
            />
          ) : tab === 'documents' ? (
            <DocumentsList entityType="intervention" entityId={interventionId} />
          ) : tab === 'evaluation' ? (
            <EvaluationTab
              detail={detail}
              currentUserId={user?.id ?? null}
              onEvaluer={() => setEvaluerOpen(true)}
            />
          ) : (
            <InterventionHistoryTimeline interventionId={interventionId} />
          )}
        </div>
      </aside>

      {editOpen && (
        <InterventionFormModal
          interventionId={interventionId}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}

      {workflowAction === 'affecter' && (
        <AffecterModal
          interventionId={interventionId}
          onClose={() => setWorkflowAction(null)}
          onSaved={onWorkflowSaved}
        />
      )}
      {workflowAction === 'planifier' && (
        <PlanifierModal
          interventionId={interventionId}
          onClose={() => setWorkflowAction(null)}
          onSaved={onWorkflowSaved}
        />
      )}
      {workflowAction === 'demarrer' && (
        <DemarrerModal
          interventionId={interventionId}
          onClose={() => setWorkflowAction(null)}
          onSaved={onWorkflowSaved}
        />
      )}
      {workflowAction === 'pause' && (
        <PauseModal
          interventionId={interventionId}
          onClose={() => setWorkflowAction(null)}
          onSaved={onWorkflowSaved}
        />
      )}
      {workflowAction === 'cloturer' && (
        <CloturerModal
          interventionId={interventionId}
          onClose={() => setWorkflowAction(null)}
          onSaved={onWorkflowSaved}
        />
      )}
      {workflowAction === 'annuler' && (
        <AnnulerModal
          interventionId={interventionId}
          onClose={() => setWorkflowAction(null)}
          onSaved={onWorkflowSaved}
        />
      )}

      {evaluerOpen && (
        <EvaluerModal
          interventionId={interventionId}
          onClose={() => setEvaluerOpen(false)}
          onSaved={() => {
            setEvaluerOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}
    </ModalPortal>
  );
}

// ─── Tabs ──────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'text-primary-600 border-primary-500 bg-primary-50/50'
          : 'text-gray-600 border-transparent hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function InfoTab({ detail }: { detail: InterventionDetail }) {
  return (
    <div className="space-y-4">
      <Section title="Identité">
        <dl className="text-sm space-y-1.5">
          <Row label="N° demande" value={detail.numero_demande} />
          <Row label="Titre" value={detail.titre} />
          <Row label="Date demande" value={new Date(detail.date_demande).toLocaleString()} />
        </dl>
        <div className="mt-2 rounded border border-gray-100 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap">
          {detail.description}
        </div>
      </Section>

      <Section title="Classification">
        <dl className="text-sm space-y-1.5">
          <Row label="Type" value={typeDemandeLabel(detail.type_demande)} />
          <Row label="Priorité" value={<PrioriteBadge priorite={detail.priorite} />} />
          <Row label="Statut" value={<StatutInterventionBadge statut={detail.statut} />} />
        </dl>
      </Section>

      <Section title="Demandeur">
        <dl className="text-sm space-y-1.5">
          <Row
            label="Demandeur"
            value={
              detail.demandeur
                ? `${detail.demandeur.first_name} ${detail.demandeur.last_name}`
                : '—'
            }
          />
          <Row
            label="Service"
            value={
              detail.service_demandeur ? (
                <span className="inline-flex items-center">
                  <Building className="w-3 h-3 mr-1 text-gray-400" />
                  {detail.service_demandeur.code} — {detail.service_demandeur.nom_complet}
                </span>
              ) : (
                '—'
              )
            }
          />
        </dl>
      </Section>

      <Section title="Localisation">
        <dl className="text-sm space-y-1.5">
          <Row
            label="Équipement"
            value={
              detail.equipement
                ? `${detail.equipement.code_equipement} — ${detail.equipement.designation}`
                : '—'
            }
          />
        </dl>
      </Section>

      <Section title="Affectation">
        <dl className="text-sm space-y-1.5">
          <Row label="Mode" value={detail.mode_affectation ?? '—'} />
          <Row
            label="Technicien"
            value={
              detail.technicien
                ? `${detail.technicien.first_name} ${detail.technicien.last_name}`
                : '—'
            }
          />
          <Row
            label="Prestataire"
            value={
              detail.prestataire
                ? detail.prestataire.nom +
                  (detail.prestataire.specialite ? ` (${detail.prestataire.specialite})` : '')
                : '—'
            }
          />
          <Row
            label="Équipe"
            value={
              detail.equipe_affectee.length > 0 ? `${detail.equipe_affectee.length} membre(s)` : '—'
            }
          />
          <Row
            label="Date affectation"
            value={
              detail.date_affectation ? new Date(detail.date_affectation).toLocaleString() : '—'
            }
          />
          <Row
            label="Date planifiée"
            value={
              detail.date_planifiee ? (
                <span className="inline-flex items-center">
                  <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                  {new Date(detail.date_planifiee).toLocaleString()}
                </span>
              ) : (
                '—'
              )
            }
          />
          <Row label="Durée estimée" value={formatDuration(detail.duree_estimee)} />
        </dl>
      </Section>

      <Section title="Exécution">
        <dl className="text-sm space-y-1.5">
          <Row
            label="Début"
            value={
              detail.date_debut_intervention
                ? new Date(detail.date_debut_intervention).toLocaleString()
                : '—'
            }
          />
          <Row
            label="Fin"
            value={
              detail.date_fin_intervention
                ? new Date(detail.date_fin_intervention).toLocaleString()
                : '—'
            }
          />
          <Row label="Durée réelle" value={formatDuration(detail.duree_reelle)} />
        </dl>
      </Section>

      {(detail.cout_main_oeuvre !== null ||
        detail.cout_pieces !== null ||
        detail.cout_deplacement !== null ||
        detail.cout_total !== null) && (
        <Section title="Coûts">
          <dl className="text-sm space-y-1.5">
            <Row
              label="Main d'œuvre"
              value={detail.cout_main_oeuvre !== null ? detail.cout_main_oeuvre.toFixed(2) : '—'}
            />
            <Row
              label="Pièces"
              value={detail.cout_pieces !== null ? detail.cout_pieces.toFixed(2) : '—'}
            />
            <Row
              label="Déplacement"
              value={detail.cout_deplacement !== null ? detail.cout_deplacement.toFixed(2) : '—'}
            />
            <Row
              label="Total"
              value={
                <span className="font-bold">
                  {detail.cout_total !== null ? detail.cout_total.toFixed(2) : '—'}
                </span>
              }
            />
          </dl>
        </Section>
      )}

      {(detail.diagnostic_initial ||
        detail.diagnostic_final ||
        detail.cause_panne ||
        detail.actions_realisees ||
        detail.etat_final) && (
        <Section title="Diagnostic & résolution">
          {detail.diagnostic_initial && (
            <SubField label="Diagnostic initial">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {detail.diagnostic_initial}
              </p>
            </SubField>
          )}
          {detail.diagnostic_final && (
            <SubField label="Diagnostic final">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.diagnostic_final}</p>
            </SubField>
          )}
          {detail.cause_panne && (
            <SubField label="Cause de la panne">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.cause_panne}</p>
            </SubField>
          )}
          {detail.actions_realisees && (
            <SubField label="Actions réalisées">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {detail.actions_realisees}
              </p>
            </SubField>
          )}
          {detail.etat_final && (
            <SubField label="État final">
              <EtatFinalBadge etat={detail.etat_final} />
            </SubField>
          )}
        </Section>
      )}

      {Object.keys(detail.custom_fields ?? {}).length > 0 && (
        <Section title="Champs personnalisés">
          <dl className="text-sm space-y-1.5">
            {Object.entries(detail.custom_fields).map(([k, v]) => (
              <Row key={k} label={k} value={String(v ?? '—')} />
            ))}
          </dl>
        </Section>
      )}

      <section className="pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          Créée le {new Date(detail.created_at).toLocaleString()}
          <br />
          Modifiée le {new Date(detail.updated_at).toLocaleString()}
        </p>
      </section>
    </div>
  );
}

function WorkflowTab({
  detail,
  canUpdate,
  reprendreLoading,
  onAction,
  onReprendre,
}: {
  detail: InterventionDetail;
  canUpdate: boolean;
  reprendreLoading: boolean;
  onAction(a: WorkflowAction): void;
  onReprendre(): void;
}) {
  if (!canUpdate) {
    return (
      <p className="text-sm text-gray-500 italic">
        Vous n'avez pas la permission d'agir sur le workflow de cette intervention.
      </p>
    );
  }

  const buttons: Array<{
    key: WorkflowAction | 'reprendre';
    label: string;
    icon: typeof Wrench;
    tone: 'primary' | 'green' | 'red' | 'orange' | 'teal';
    onClick(): void;
  }> = [];

  const statut = detail.statut;
  if (
    statut === 'nouveau' ||
    statut === 'en_attente_validation' ||
    statut === 'affecte' ||
    statut === 'planifie'
  ) {
    buttons.push({
      key: 'affecter',
      label: statut === 'affecte' ? 'Réaffecter' : 'Affecter',
      icon: UserCheck,
      tone: 'primary',
      onClick: () => onAction('affecter'),
    });
  }
  if (statut === 'affecte' || statut === 'planifie') {
    buttons.push({
      key: 'planifier',
      label: statut === 'planifie' ? 'Replanifier' : 'Planifier',
      icon: Calendar,
      tone: 'primary',
      onClick: () => onAction('planifier'),
    });
  }
  if (statut === 'affecte' || statut === 'planifie') {
    buttons.push({
      key: 'demarrer',
      label: 'Démarrer',
      icon: PlayCircle,
      tone: 'teal',
      onClick: () => onAction('demarrer'),
    });
  }
  if (statut === 'en_cours') {
    buttons.push({
      key: 'pause',
      label: 'Mettre en pause',
      icon: PauseCircle,
      tone: 'orange',
      onClick: () => onAction('pause'),
    });
    buttons.push({
      key: 'cloturer',
      label: 'Clôturer',
      icon: CheckCircle,
      tone: 'green',
      onClick: () => onAction('cloturer'),
    });
  }
  if (statut === 'en_attente_pieces') {
    buttons.push({
      key: 'reprendre',
      label: 'Reprendre',
      icon: PlayCircle,
      tone: 'teal',
      onClick: onReprendre,
    });
  }
  if (statut === 'en_attente_validation_finale') {
    buttons.push({
      key: 'cloturer',
      label: 'Clôturer définitivement',
      icon: CheckCircle,
      tone: 'green',
      onClick: () => onAction('cloturer'),
    });
  }
  if (statut !== 'cloture' && statut !== 'annule') {
    buttons.push({
      key: 'annuler',
      label: 'Annuler',
      icon: Ban,
      tone: 'red',
      onClick: () => onAction('annuler'),
    });
  }

  if (statut === 'cloture' || statut === 'annule') {
    return (
      <div className="text-sm text-gray-500">
        <p className="flex items-center">
          {statut === 'cloture' ? (
            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
          ) : (
            <XCircle className="w-4 h-4 mr-2 text-gray-500" />
          )}
          L'intervention est {statut === 'cloture' ? 'clôturée' : 'annulée'}. Aucune action de
          workflow n'est disponible.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        Statut courant :{' '}
        <span className="font-medium text-gray-800">
          <StatutInterventionBadge statut={statut} />
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        {buttons.map((b) => (
          <button
            key={b.key}
            onClick={b.onClick}
            disabled={b.key === 'reprendre' && reprendreLoading}
            className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
              b.tone === 'primary'
                ? 'bg-primary-500 hover:bg-primary-600'
                : b.tone === 'green'
                  ? 'bg-green-500 hover:bg-green-600'
                  : b.tone === 'red'
                    ? 'bg-red-500 hover:bg-red-600'
                    : b.tone === 'orange'
                      ? 'bg-orange-500 hover:bg-orange-600'
                      : 'bg-teal-500 hover:bg-teal-600'
            }`}
          >
            {b.key === 'reprendre' && reprendreLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <b.icon className="w-4 h-4 mr-2" />
            )}
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function EvaluationTab({
  detail,
  currentUserId,
  onEvaluer,
}: {
  detail: InterventionDetail;
  currentUserId: string | null;
  onEvaluer(): void;
}) {
  const hasEvaluation = detail.note_satisfaction !== null;
  const canEvaluate =
    detail.statut === 'cloture' &&
    !hasEvaluation &&
    currentUserId !== null &&
    currentUserId === detail.demandeur_id;

  if (!hasEvaluation && !canEvaluate) {
    return (
      <div className="text-sm text-gray-500">
        {detail.statut !== 'cloture' ? (
          <p>L'intervention doit être clôturée avant de pouvoir être évaluée par le demandeur.</p>
        ) : (
          <p>
            Seul le demandeur ({detail.demandeur?.first_name} {detail.demandeur?.last_name}) peut
            évaluer cette intervention.
          </p>
        )}
      </div>
    );
  }

  if (!hasEvaluation && canEvaluate) {
    return (
      <div className="text-sm text-gray-700 space-y-3">
        <p>Cette intervention n'a pas encore été évaluée.</p>
        <button
          onClick={onEvaluer}
          className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
        >
          <Star className="w-4 h-4 mr-2" />
          Évaluer maintenant
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Section title="Note globale">
        <Stars value={detail.note_satisfaction ?? 0} large />
      </Section>
      <Section title="Sous-notes">
        <dl className="text-sm space-y-1.5">
          <Row label="Délai" value={<Stars value={detail.evaluation_delai ?? 0} />} />
          <Row label="Qualité" value={<Stars value={detail.evaluation_qualite ?? 0} />} />
          <Row label="Comportement" value={<Stars value={detail.evaluation_comportement ?? 0} />} />
        </dl>
      </Section>
      {detail.commentaire_satisfaction && (
        <Section title="Commentaire">
          <p className="text-sm text-gray-700 italic whitespace-pre-wrap">
            "{detail.commentaire_satisfaction}"
          </p>
        </Section>
      )}
      {detail.date_evaluation && (
        <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
          Évaluée le {new Date(detail.date_evaluation).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function Stars({ value, large }: { value: number; large?: boolean }) {
  const size = large ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <span className="inline-flex items-center">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= value ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
        />
      ))}
      {large && <span className="ml-2 text-sm font-medium text-gray-700">{value}/5</span>}
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
      {children}
    </section>
  );
}

function SubField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-2">
      <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500 text-xs">{label}</dt>
      <dd className="text-gray-900 text-right break-all">{value}</dd>
    </div>
  );
}

// Re-export pour les imports externes utilisant l'icône.
export { Clock };
